import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  accentStorageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
  accentColor: '#f97316',
  setAccentColor: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

// Convert hex to HSL values (returns "h s% l%" format for CSS variables)
function hexToHSL(hex: string): { h: number; s: number; l: number } {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex values
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

// Apply accent color CSS variables to the document
function applyAccentColor(hex: string) {
  const root = window.document.documentElement;
  const { h, s, l } = hexToHSL(hex);
  
  // Set CSS variables for primary/accent colors
  root.style.setProperty('--primary', `${h} ${s}% ${l}%`);
  root.style.setProperty('--ring', `${h} ${s}% ${l}%`);
  
  // Generate and set blaze color palette (50-950)
  const generateShade = (targetL: number) => `${h} ${s}% ${targetL}%`;
  
  // Calculate lightness stops based on the accent color (assumed to be the 500 shade)
  // We interpolate from the accent lightness to 98% (for 50) and 5% (for 950)
  const lightenStep = (98 - l) / 5;
  const darkenStep = (l - 5) / 5;

  root.style.setProperty('--blaze-50', generateShade(98));
  root.style.setProperty('--blaze-100', generateShade(l + lightenStep * 4));
  root.style.setProperty('--blaze-200', generateShade(l + lightenStep * 3));
  root.style.setProperty('--blaze-300', generateShade(l + lightenStep * 2));
  root.style.setProperty('--blaze-400', generateShade(l + lightenStep));
  root.style.setProperty('--blaze-500', generateShade(l));
  root.style.setProperty('--blaze-600', generateShade(l - darkenStep));
  root.style.setProperty('--blaze-700', generateShade(l - darkenStep * 2));
  root.style.setProperty('--blaze-800', generateShade(l - darkenStep * 3));
  root.style.setProperty('--blaze-900', generateShade(l - darkenStep * 4));
  root.style.setProperty('--blaze-950', generateShade(5));
  
  // Also update the blaze gradient for branding consistency
  const darkerHex = adjustBrightness(hex, -15);
  const darkestHex = adjustBrightness(hex, -30);
  root.style.setProperty('--accent-gradient', `linear-gradient(135deg, ${hex} 0%, ${darkerHex} 50%, ${darkestHex} 100%)`);
}

// Adjust brightness of a hex color
function adjustBrightness(hex: string, percent: number): string {
  hex = hex.replace(/^#/, '');
  
  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);
  
  r = Math.max(0, Math.min(255, r + (r * percent / 100)));
  g = Math.max(0, Math.min(255, g + (g * percent / 100)));
  b = Math.max(0, Math.min(255, b + (b * percent / 100)));
  
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  accentStorageKey = 'vite-ui-accent',
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );
  
  const [accentColor, setAccentColorState] = useState<string>(
    () => localStorage.getItem(accentStorageKey) || '#f97316'
  );

  // Apply theme class
  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  // Apply accent color on mount and when it changes
  useEffect(() => {
    applyAccentColor(accentColor);
  }, [accentColor]);

  const setAccentColor = useCallback((color: string) => {
    localStorage.setItem(accentStorageKey, color);
    setAccentColorState(color);
    applyAccentColor(color);
  }, [accentStorageKey]);

  const handleSetTheme = useCallback((newTheme: Theme) => {
    localStorage.setItem(storageKey, newTheme);
    setTheme(newTheme);
  }, [storageKey]);

  const value = useMemo(() => ({
    theme,
    setTheme: handleSetTheme,
    accentColor,
    setAccentColor,
  }), [theme, handleSetTheme, accentColor, setAccentColor]);

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
}

